import { useMsal } from "@azure/msal-react";
import { AxiosError, AxiosResponse } from "axios";
import { client } from "cache/cache";
import { ErrorToastContent } from "error/ErrorToastContent";
import {
  isAuthRedirectError,
  redirectToLoginFromAuthFailure,
} from "features/Authentication/authRecovery";
import { Abbreviate } from "helpers/string-helpers";
import { useEffect, useMemo } from "react";
import ax from "services/AxiosService";
import { Alert, AlertType } from "shared-components/toast";

function isAxiosError(error: unknown): error is AxiosError {
  return Boolean((error as AxiosError)?.isAxiosError);
}

export const useAxiosInterceptor = (): void => {
  const { instance } = useMsal();

  const interceptors = useMemo(
    () => ({
      response: (response: AxiosResponse) => response,
      error: (error: unknown) => {
        if (isAuthRedirectError(error)) {
          return Promise.reject(error);
        }

        if (!isAxiosError(error)) {
          Alert({
            type: AlertType.ERROR,
            message:
              error instanceof Error
                ? error.message
                : "Failed to acquire valid auth token.",
          });
          return Promise.reject(error);
        }

        const status = error.response?.status;
        if (status === 401 && window.location.pathname !== "/login") {
          try {
            redirectToLoginFromAuthFailure({
              instance: instance,
              clearApolloStore: () => client.clearStore(),
            });
          } catch (authError) {
            return Promise.reject(authError);
          }
        } else {
          Alert({
            type: AlertType.ERROR,
            hoc: () => (
              <ErrorToastContent
                shortErrorMessage={Abbreviate(
                  error.response?.statusText ?? "Server not responding",
                  100
                )}
                longErrorMessage={
                  error.response?.statusText ?? "Server not responding"
                }
              />
            ),
            longMessage: error.response?.statusText ?? "Server not responding",
          });
        }
        return Promise.reject(error);
      },
    }),
    [instance]
  );
  useEffect(() => {
    const id = ax.interceptors.response.use(
      interceptors.response,
      interceptors.error
    );
    return () => ax.interceptors.response.eject(id);
  }, [interceptors]);
};
